import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { getDb } from '@/lib/firebase-config';
import type { AppSettings } from '../types/settings';
import { defaultDrillCardPreferences } from '@/components/drill-library/types/drill-library-settings';
import { DEFAULT_POSTURE_ORIENTATION_STRATEGY_ID } from '@/lib/posture-analysis/posture-pic-evaluation/strategies/posture-orientation-strategy-id';

// Default settings - centralized definition
export const defaultSettings: AppSettings = {
    unit: 'cm',
    showGrid: true,
    gridSize: 3,
    goniometerColor: '#ffc800',
    goniometerPivotColor: '#ffffff',
    goniometerThickness: 4,
    analysisThreshold: 20,
    maxWidth: '720px',
    aiProvider: 'gemini',
    geminiApiKey: '',
    chatGptApiKey: '',
    anthropicApiKey: '',
    allowContact: false,
    favoriteDrills: [],
    drillFilters: {
        activeFilters: {},
        showFavoritesOnly: false,
    },
    drillLayoutMode: 'grid',
    drillGridColumns: 3,
    drillCardPreferences: defaultDrillCardPreferences,
    showPostureAnalysisTips: true,
    showRemoteAssessmentSteps: true,
    experimentalFeaturesEnabled: false,
    useLegacySopAssessment: false,
    isAdminMode: false,
    postureAnalysisPanelEnabled: true,
    postureOrientationStrategyId: DEFAULT_POSTURE_ORIENTATION_STRATEGY_ID,
    saveSelfAssessmentPictures: true,
};
import logger from '@/lib/logging';

function normalizeBooleanSetting(value: unknown): boolean | undefined {
    if (value === undefined || value === null) {
        return undefined;
    }
    if (typeof value === 'boolean') {
        return value;
    }
    if (typeof value === 'string') {
        return value === 'true';
    }
    return Boolean(value);
}

/**
 * Checks if the given settings differ from defaults
 * Note: experimentalFeaturesEnabled and isAdminMode are always considered "different"
 * when present, even if they match defaults, to ensure they're saved.
 */
function settingsDifferFromDefaults(settings: AppSettings): boolean {
    const alwaysSaveKeys: (keyof AppSettings)[] = [
        'experimentalFeaturesEnabled',
        'useLegacySopAssessment',
        'isAdminMode',
        'postureAnalysisPanelEnabled',
    ];

    // Check if any always-save keys are present
    if (alwaysSaveKeys.some(key => settings[key] !== undefined)) {
        return true;
    }

    // Check if any other settings differ from defaults
    return Object.keys(settings).some(key => {
        const settingKey = key as keyof AppSettings;
        return settings[settingKey] !== defaultSettings[settingKey];
    });
}

/**
 * Gets only the settings that differ from defaults
 * Note: experimentalFeaturesEnabled and isAdminMode are always saved when present,
 * even if they match defaults, to ensure they're persisted in the database.
 */
function getDifferingSettings(settings: AppSettings): Partial<AppSettings> {
    const differing: Partial<AppSettings> = {};
    const alwaysSaveKeys: (keyof AppSettings)[] = [
        'experimentalFeaturesEnabled',
        'useLegacySopAssessment',
        'isAdminMode',
        'postureAnalysisPanelEnabled',
    ];

    Object.keys(settings).forEach(key => {
        const settingKey = key as keyof AppSettings;
        const defaultValue = defaultSettings[settingKey];
        const currentValue = settings[settingKey];

        // Always save these keys when they exist in settings, even if they match defaults
        if (alwaysSaveKeys.includes(settingKey) && currentValue !== undefined) {
            (differing as any)[settingKey] = currentValue;
        } else if (currentValue !== defaultValue) {
            (differing as any)[settingKey] = currentValue;
        }
    });

    return differing;
}

/**
 * Loads user settings from Firebase
 * @param userId The authenticated user's ID
 * @returns The user's settings merged with defaults
 */
export async function loadUserSettings(userId: string): Promise<AppSettings> {
    try {
        const db = getDb();
        const settingsRef = doc(db, 'users', userId, 'settings', 'preferences');
        const settingsDoc = await getDoc(settingsRef);

        if (settingsDoc.exists()) {
            const savedSettings = settingsDoc.data() as Partial<AppSettings>;
            if (savedSettings.isAdminMode !== undefined) {
                savedSettings.isAdminMode = normalizeBooleanSetting(savedSettings.isAdminMode);
            }

            // Preferences are canonical, but older saves also mirrored flags on /users/{uid}.
            if (savedSettings.isAdminMode === undefined) {
                const userDoc = await getDoc(doc(db, 'users', userId));
                const mirroredAdminMode = normalizeBooleanSetting(userDoc.data()?.isAdminMode);
                if (mirroredAdminMode !== undefined) {
                    savedSettings.isAdminMode = mirroredAdminMode;
                }
            }

            // Merge saved settings with defaults
            return { ...defaultSettings, ...savedSettings };
        }

        return defaultSettings;
    } catch (error) {
        logger.error('Error loading user settings from Firebase:', error);
        // Fall back to localStorage if available
        return loadSettingsFromLocalStorage();
    }
}

/**
 * Saves user settings to Firebase
 * Only saves if settings differ from defaults
 * @param userId The authenticated user's ID
 * @param settings The settings to save
 */
export async function saveUserSettings(userId: string, settings: AppSettings): Promise<boolean> {
    try {
        const db = getDb();
        const settingsRef = doc(db, 'users', userId, 'settings', 'preferences');

        if (settingsDifferFromDefaults(settings)) {
            // Only save the settings that differ from defaults
            const differingSettings = getDifferingSettings(settings);
            await setDoc(settingsRef, {
                ...differingSettings,
                lastUpdated: new Date().toISOString()
            });
        } else {
            // If all settings are default, delete the document to save storage
            await deleteDoc(settingsRef).catch(() => {
                // Document might not exist, that's fine
            });
        }

        // Also save to localStorage for offline/quick access
        saveSettingsToLocalStorage(settings);

        // Best-effort mirror. Preferences are what the app reloads. A denied
        // profile write must not fail the settings save that already landed.
        try {
            const userDocRef = doc(db, 'users', userId);
            const featureFlags: Record<string, boolean> = {};

            if (settings.experimentalFeaturesEnabled !== undefined) {
                featureFlags.experimentalFeaturesEnabled = settings.experimentalFeaturesEnabled;
            }

            if (settings.isAdminMode !== undefined) {
                featureFlags.isAdminMode = settings.isAdminMode;
            }

            if (Object.keys(featureFlags).length > 0) {
                await setDoc(userDocRef, featureFlags, { merge: true });
            }
        } catch (mirrorError) {
            logger.error('Error mirroring feature flags onto the user profile:', mirrorError);
        }

        return true;
    } catch (error) {
        logger.error('Error saving user settings to Firebase:', error);
        // Fall back to localStorage. Callers must treat this as not persisted:
        // a later load reads Firestore and ignores this copy when the doc is missing.
        saveSettingsToLocalStorage(settings);
        return false;
    }
}

/**
 * Loads settings from localStorage (fallback)
 */
export function loadSettingsFromLocalStorage(): AppSettings {
    try {
        const savedSettings = localStorage.getItem('appSettings');
        const postureAnalysisUnit = localStorage.getItem('postureAnalysisUnit') as 'cm' | 'inches';

        let settings = defaultSettings;

        if (savedSettings) {
            const parsed = JSON.parse(savedSettings) as Partial<AppSettings>;
            if (parsed.isAdminMode !== undefined) {
                parsed.isAdminMode = normalizeBooleanSetting(parsed.isAdminMode);
            }
            settings = { ...defaultSettings, ...parsed };
        }

        // Handle legacy postureAnalysisUnit
        if (postureAnalysisUnit && (postureAnalysisUnit === 'cm' || postureAnalysisUnit === 'inches')) {
            settings.unit = postureAnalysisUnit;
        }

        return settings;
    } catch (error) {
        logger.error('Error loading settings from localStorage:', error);
        return defaultSettings;
    }
}

/**
 * Saves settings to localStorage (fallback or quick access)
 */
export function saveSettingsToLocalStorage(settings: AppSettings): void {
    try {
        localStorage.setItem('appSettings', JSON.stringify(settings));
        localStorage.setItem('postureAnalysisUnit', settings.unit); // Keep for backward compatibility
    } catch (error) {
        logger.error('Error saving settings to localStorage:', error);
    }
}

/**
 * Migrates existing localStorage settings to Firebase for a user
 * Called when a user logs in and has localStorage settings but no Firebase settings
 */
export async function migrateLocalSettingsToFirebase(userId: string): Promise<void> {
    try {
        const localSettings = loadSettingsFromLocalStorage();

        // Check if user already has Firebase settings
        const db = getDb();
        const settingsRef = doc(db, 'users', userId, 'settings', 'preferences');
        const existingDoc = await getDoc(settingsRef);

        // Only migrate if no Firebase settings exist and local settings differ from defaults
        if (!existingDoc.exists() && settingsDifferFromDefaults(localSettings)) {
            await saveUserSettings(userId, localSettings);
            logger.debug('Migrated local settings to Firebase for user:', userId);
        }
    } catch (error) {
        logger.error('Error migrating settings to Firebase:', error);
    }
}

